import { createFileRoute, redirect } from '@tanstack/react-router';
import { AuthError } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import RiotIcon from '@/assets/icons/riot.svg?react';
import { useSignInWithRiot } from '@/features/auth/hooks/useSignInWithRiot';
import { useValidateInvite } from '@/features/invites/hooks/useValidateInvite';
import {
  getPendingInviteToken,
  isInInviteFlow,
} from '@/features/invites/utils/pendingInvite';
import { getMyUserOptions } from '@/features/users/hooks/useGetMyUser/getMyUser.options';
import { Button } from '@/shared/components/ui/button';
import { WelcomeLayout } from '@/shared/components/welcomeLayout';
import { captureAttribution } from '@/shared/utils/attribution';
import { isMobileWeb } from '@/shared/utils/mobileWeb';
import { clearAllStorage } from '@/shared/utils/storage';

const searchSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
  redirectTo: z.string().optional(),
  // UTM params for tracking (passed from invite flow)
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: Login,
  beforeLoad: async ({ context, location }) => {
    // Redirect mobile web users to app store download page
    // Skip redirect if user is in invite flow (has pending invite token/data)
    if (
      isMobileWeb() &&
      !isInInviteFlow() &&
      location.pathname !== '/get-app'
    ) {
      throw redirect({ to: '/get-app', replace: true });
    }

    let user = undefined;
    try {
      user = await context.queryClient.ensureQueryData(getMyUserOptions());
    } catch {
      /* empty */
    }
    if (user) {
      throw redirect({
        to: '/onboarding',
      });
    }
  },
});

function Login() {
  const { error, error_description } = Route.useSearch();
  const [pendingToken] = useState(() => getPendingInviteToken());

  // Validate pending invite if exists
  const { data: inviteValidation } = useValidateInvite({
    token: pendingToken ?? '',
    options: {
      enabled: !!pendingToken,
    },
  });

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        toast.error('Something went wrong', {
          id: 'oauth-error',
          description: (error_description ?? error)?.trim(),
          duration: 10_000,
        });
      });
    }
  }, [error, error_description]);

  const { mutate } = useSignInWithRiot();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    captureAttribution();
  }, []);

  const hasValidInvite = pendingToken && inviteValidation?.valid;

  const handleSignInWithRiot = () => {
    setIsLoading(true);
    mutate(undefined, {
      onError: err => {
        if (
          err instanceof AuthError &&
          err.name === 'UserAlreadyAuthenticatedException'
        ) {
          clearAllStorage();
          mutate();
          return;
        }
        setIsLoading(false);
      },
    });
  };

  return (
    <WelcomeLayout.Root>
      <WelcomeLayout.Background />
      <WelcomeLayout.Container>
        <WelcomeLayout.Logo />

        <WelcomeLayout.Content>
          {hasValidInvite ? (
            <div className="flex flex-col gap-2.5 text-center">
              <h1 className="text-3xl leading-tight font-black tracking-tight text-white italic md:text-5xl">
                {inviteValidation.inviterDisplayName}
                <br />
                wants to be your duo!
              </h1>
              <p className="text-cold-50 text-base leading-tight font-semibold tracking-wide">
                Create your free Gankster Duo account to accept the invite &
                unlock your shared level, stats, and progress together.
              </p>
            </div>
          ) : (
            <WelcomeLayout.Header />
          )}

          <WelcomeLayout.Actions>
            <Button
              variant="riot"
              onClick={handleSignInWithRiot}
              size="lg"
              className="w-full"
              loading={isLoading}
            >
              <RiotIcon />
              Continue with Riot
            </Button>
          </WelcomeLayout.Actions>

          <WelcomeLayout.Legal className="mt-12 md:mt-0" />
        </WelcomeLayout.Content>
      </WelcomeLayout.Container>
    </WelcomeLayout.Root>
  );
}
